import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { procurementService, PurchaseBatch, PurchaseListRow } from '../../services/procurementService';
import { formatCurrencyBDT } from '../../utils/currency';

interface ProcurementStats {
  totalPurchases: number;
  pendingPurchases: number;
  confirmedPurchases: number;
  receivedPurchases: number;
  totalValue: number;
  expiringBatches: number;
  qualityCheckItems: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`procurement-tabpanel-${index}`}
      aria-labelledby={`procurement-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ProcurementDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState<ProcurementStats>({
    totalPurchases: 0,
    pendingPurchases: 0,
    confirmedPurchases: 0,
    receivedPurchases: 0,
    totalValue: 0,
    expiringBatches: 0,
    qualityCheckItems: 0
  });
  const [recentPurchases, setRecentPurchases] = useState<PurchaseListRow[]>([]);
  const [expiringBatches, setExpiringBatches] = useState<PurchaseBatch[]>([]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel. Counts and value come from the
      // database (never capped at a page); recent orders are lean rows.
      const [
        purchaseStats,
        recentPage,
        expiringBatchesData,
        qualityCheckItems
      ] = await Promise.all([
        procurementService.getPurchaseStats(),
        procurementService.searchPurchases({ limit: 10 }),
        procurementService.getExpiringBatches(30),
        procurementService.getItemsRequiringQualityCheck({ limit: 100 })
      ]);

      const counts = purchaseStats.counts_by_status;
      setStats({
        totalPurchases: purchaseStats.total_count,
        pendingPurchases: counts.PENDING ?? 0,
        confirmedPurchases: counts.CONFIRMED ?? 0,
        receivedPurchases: counts.RECEIVED ?? 0,
        totalValue: Number(purchaseStats.total_value),
        expiringBatches: expiringBatchesData.length,
        qualityCheckItems: qualityCheckItems.length
      });

      setRecentPurchases(recentPage.items);
      setExpiringBatches(expiringBatchesData.slice(0, 5));

    } catch (err) {
      console.error('Error loading procurement dashboard:', err);
      setError('Failed to load procurement data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'CONFIRMED': return 'info';
      case 'RECEIVED': return 'success';
      case 'PARTIAL_RECEIVED': return 'primary';
      case 'CANCELLED': return 'error';
      case 'ON_HOLD': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING': return <ScheduleIcon fontSize="small" />;
      case 'CONFIRMED': return <AssignmentIcon fontSize="small" />;
      case 'RECEIVED': return <CheckCircleIcon fontSize="small" />;
      case 'PARTIAL_RECEIVED': return <ShippingIcon fontSize="small" />;
      default: return <InventoryIcon fontSize="small" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" }, mb: 0 }}>
          Procurement Dashboard
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="Refresh Data">
            <IconButton onClick={loadDashboardData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/procurement/purchases/create')}
            sx={{ ml: 1 }}
          >
            New Purchase Order
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, 
        gap: 3, 
        mb: 4 
      }}>
        <Box>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Purchases
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalPurchases}
                  </Typography>
                </Box>
                <InventoryIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Pending Orders
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.pendingPurchases}
                  </Typography>
                </Box>
                <Badge badgeContent={stats.pendingPurchases} color="warning">
                  <ScheduleIcon color="warning" sx={{ fontSize: 40 }} />
                </Badge>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Value
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatCurrencyBDT(stats.totalValue)}
                  </Typography>
                </Box>
                <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Expiring Batches
                  </Typography>
                  <Typography variant="h4" color={stats.expiringBatches > 0 ? "error.main" : "text.primary"}>
                    {stats.expiringBatches}
                  </Typography>
                </Box>
                <Badge badgeContent={stats.expiringBatches} color="error">
                  <WarningIcon color={stats.expiringBatches > 0 ? "error" : "disabled"} sx={{ fontSize: 40 }} />
                </Badge>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Quick Actions */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
        gap: 3, 
        mb: 4 
      }}>
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: 2 
              }}>
                <Box>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/procurement/suppliers/create')}
                  >
                    Add Supplier
                  </Button>
                </Box>
                <Box>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<InventoryIcon />}
                    onClick={() => navigate('/procurement/purchases')}
                  >
                    View All Orders
                  </Button>
                </Box>
                <Box>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AssignmentIcon />}
                    onClick={() => navigate('/procurement/quality-check')}
                    disabled={stats.qualityCheckItems === 0}
                  >
                    Quality Check ({stats.qualityCheckItems})
                  </Button>
                </Box>
                <Box>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<WarningIcon />}
                    onClick={() => navigate('/procurement/batches/expiring')}
                    color={stats.expiringBatches > 0 ? "error" : "primary"}
                  >
                    Expiring Batches ({stats.expiringBatches})
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status Overview
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: 2 
              }}>
                <Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="warning.main">
                      {stats.pendingPurchases}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Pending
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="info.main">
                      {stats.confirmedPurchases}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Confirmed
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {stats.receivedPurchases}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Received
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main">
                      {((stats.receivedPurchases / Math.max(stats.totalPurchases, 1)) * 100).toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Completion Rate
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Tabs for Recent Data */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="procurement tabs">
            <Tab label="Recent Purchase Orders" />
            <Tab label="Expiring Batches" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Recent Purchase Orders
          </Typography>
          {recentPurchases.length === 0 ? (
            <Typography color="textSecondary">No purchase orders found.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recentPurchases.map((purchase) => (
                <Box key={purchase.purchase_id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="h6">
                            {purchase.purchase_number}
                          </Typography>
                          <Typography color="textSecondary">
                            {purchase.supplier?.name || 'Unknown Supplier'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {new Date(purchase.purchase_date).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="h6" color="primary">
                            {formatCurrencyBDT(Number(purchase.net_payable ?? purchase.total_amount))}
                          </Typography>
                          <Chip
                            icon={getStatusIcon(purchase.status)}
                            label={purchase.status}
                            color={getStatusColor(purchase.status) as any}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          )}
          <Box mt={2}>
            <Button
              variant="outlined"
              onClick={() => navigate('/procurement/purchases')}
            >
              View All Purchase Orders
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Expiring Batches (Next 30 Days)
          </Typography>
          {expiringBatches.length === 0 ? (
            <Alert severity="success">No batches expiring in the next 30 days.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {expiringBatches.map((batch) => (
                <Box key={batch.batch_id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="h6">
                            Batch: {batch.batch_number}
                          </Typography>
                          <Typography color="textSecondary">
                            Quantity: {batch.quantity} units
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Location: {batch.location || 'Not specified'}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body1" color="error">
                            Expires: {batch.expiry_date ? new Date(batch.expiry_date).toLocaleDateString() : 'No date'}
                          </Typography>
                          <Chip
                            icon={<WarningIcon />}
                            label="Expiring Soon"
                            color="error"
                            size="small"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          )}
          <Box mt={2}>
            <Button
              variant="outlined"
              onClick={() => navigate('/procurement/batches/expiring')}
            >
              View All Expiring Batches
            </Button>
          </Box>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default ProcurementDashboard;
